'use client'

import { useContext, FC, useState, useRef, useEffect } from 'react'
import axios from 'axios'
import { useRouter } from 'next/router'
import MapCoreManagerContext from './Context'
import 'ol/ol.css'
import { Map } from 'ol'
import useCustomView from './hooks/useCustomView'
import useBaseLayer from './hooks/useBaseLayer'
import { useInitializeMap } from './hooks/useInitializeMap'
// import { useTooltip } from './hooks/useTooltip'
import { useLayersUpdate } from './hooks/useLayersUpdate'
import { fromLonLat } from 'ol/proj'
import { usePersonalization } from 'contexts/Personalization/Provider'
// UTILS
import { AFACodeEncoded } from './utils/AFACodeEncoded'
import { AFACodeDecode } from './utils/AFACodeDecode'
import { generateAFACodeArea } from './utils/generateAFACodeArea'
import { MapPin, OpenLayersBaseMap, PinType } from '../../types'
import { MapCoreProps } from '../../types'
import { boundingExtent } from 'ol/extent'
import VectorSource from 'ol/source/Vector'
import VectorLayer from 'ol/layer/Vector'
import encodeAfacodes from 'modules/CitizenPortal/services/calls/geo/encodeAfacodes'
import { INSTITUTION_SLUG } from 'constants/system'
// import Footer from './components/Footer'

const MapCoreManagerProvider: FC<MapCoreProps> = ({ children, ...props }) => {
  const { license } = usePersonalization()
  const DEFAULT_VIEW = {
    center: fromLonLat([license?.geo?.settings?.map_default_view?.lon, license?.geo?.settings?.map_default_view?.lat]),
    zoom: license?.geo?.settings?.map_default_view?.zoom,
  }

  const {
    defaultBaseLayerSourceName,
    defaultView = DEFAULT_VIEW,
    defaultPins,
    simpleBaseLayerToogleButton = false,
    selectionMode = 'POINT_AND_CLICK',
    markingDataCard = false,
    searchField = true,
    navbar = true,
    goToMyLocation = true,
    _fillInTheMarkingData = null,
  } = props ?? {}

  const { query } = useRouter()
  const mapRef = useRef<HTMLDivElement | null>(null) // Referência para o div do mapa
  const mapInstance = useRef<Map | null>(null) // Guarda a instância do mapa
  const selectedFeatureRef = useRef<any>(null)
  const hoveredFeatureRef = useRef<any>(null)
  const [view] = useState(useCustomView({ defaultView: defaultView }))
  const [baseLayerSourceName, setBaseLayerSourceName] = useState<OpenLayersBaseMap>(defaultBaseLayerSourceName || 'XYZ')
  const [geolocation, setGeolocation] = useState({ active: false, opacity: 100 })
  const [markingData, setMarkingData] = useState<any>(null)
  const [queryingMarkingData, setQueryingMarkingData] = useState<boolean>(false)
  //PINS
  const [selectedEntity, setSelectedEntity] = useState<{
    type: PinType
    data: any
  } | null>(null)

  const entitySource = new VectorSource()
  const entityLayer = new VectorLayer({
    source: entitySource,
    properties: {
      name: 'ENTITY_LAYER',
    },
  })
  const createBaseLayer = useBaseLayer({ baseLayerSourceName })

  //FUNÇÕES
  async function _getMarkingData({
    lat,
    lon,
    goToTheLocation = false,
  }: {
    lat: number
    lon: number
    goToTheLocation: boolean
  }) {
    // if (!_fillInTheMarkingData) return

    setQueryingMarkingData(true)

    let newData: {
      lat: number
      lon: number
      address: any | null
      geometry: any | null
      afacode: string | null
    } = { lat, lon, address: null, geometry: null, afacode: null }

    try {
      const { data } = await axios.get('https://nominatim.openstreetmap.org/reverse', {
        params: { format: 'json', lon, lat, addressdetails: 1 },
        headers: { 'Accept-Language': 'pt-BR' },
      })
      newData = { ...newData, address: { ...data } }
    } catch (err) {
      console.error(err)
    }

    try {
      try {
        const res = await encodeAfacodes({
          data: { lat, lng: lon, u: INSTITUTION_SLUG == 'mocambique' ? '2' : '4' },
        })

        const logistic_id = res.body.afacode.id
        const geometry = res.body.afacode.geometry

        newData = { ...newData, geometry, afacode: logistic_id }
      } catch (error) {
        console.warn('Erro ao buscar dados AFA:', error)
      }
    } catch (error) {
      console.warn('Erro ao buscar dados AFA:', error)
    }

    setMarkingData(newData)
    if (_fillInTheMarkingData) {
      _fillInTheMarkingData(newData)
    }
    setQueryingMarkingData(false)

    // --- IR ATÉ O LOCAL (polígono se existir; senão, ponto) ---
    if (goToTheLocation) {
      const view = mapInstance.current?.getView()
      if (!view) return

      const geom = newData.geometry

      // 1) Se houver Polygon/MultiPolygon, faz fit no extent
      if (geom?.type === 'Polygon' || geom?.type === 'MultiPolygon') {
        const coordsLonLat: [number, number][] =
          geom.type === 'Polygon'
            ? geom.coordinates?.[0] ?? [] // anel externo
            : geom.coordinates?.[0]?.[0] ?? [] // 1º polígono, anel externo

        if (coordsLonLat.length >= 2) {
          const coords3857 = coordsLonLat.map((c) => fromLonLat(c))
          const extent = boundingExtent(coords3857)

          view.fit(extent, {
            duration: 500,
            padding: [50, 50, 50, 50],
            maxZoom: 18,
          })
          return
        }
      }

      // 2) Fallback: centraliza no ponto (lat/lon) e aplica zoom
      const center = fromLonLat([lon, lat])
      view.animate({ center, duration: 500 }, { zoom: Math.max(view.getZoom() ?? 0, 17), duration: 500 })
    }
  }

  // Inicialização do mapa e eventos
  useInitializeMap({
    mapRef,
    mapInstance,
    entityLayer,
    view,
    baseLayerSourceName,
    createBaseLayer,
    // geolocation,
    selectedFeatureRef,
    hoveredFeatureRef,
    _getMarkingData,
    AFACodeHandleClick: selectionMode == 'ONLY_VIEW' ? false : selectionMode == 'POINT_AND_CLICK' ? true : false,
    pins: defaultPins,
    openDrawer: (entity: MapPin) => setSelectedEntity(entity),
  })

  // Atualizações de camadas com base em alterações de estado
  useLayersUpdate({
    mapInstance,
    baseLayerSourceName,
    createBaseLayer,
    geolocation,
  })

  useEffect(() => {
    //VERIFICA SE TEM O PARAMETRO afacode NA URL
    if (query?.afacode) {
      const queryAfaCode = query?.afacode.toString()
      // const hasJurisdiction = queryAfaCode.includes('BR~')
      // let afaCode = ''
      // if (hasJurisdiction) {
      //   afaCode = queryAfaCode
      // } else {
      //   afaCode = `BR~${queryAfaCode}`
      // }
      AFACodeDecode({
        AFA_CODE: queryAfaCode,
        mapInstance,
        view,
        _getMarkingData,
        openDrawer: (entity: MapPin) => setSelectedEntity(entity),
      })
      //VERIFICA SE TEM O PARAMETRO lat E lon NA URL
    } else if (query?.lat && query?.lon) {
      AFACodeEncoded({
        lat: query?.lat.toString(),
        lon: query?.lon.toString(),
        mapInstance,
        view,
        _getMarkingData,
        openDrawer: (entity: MapPin) => setSelectedEntity(entity),
      })
    } else if (query?.afacodeArea) {
      generateAFACodeArea({ mapInstance })
    } else {
      if (goToMyLocation && INSTITUTION_SLUG !== 'mocambique') {
        if (!navigator.geolocation) return
        navigator.geolocation.getCurrentPosition(
          ({ coords }) => {
            if (!mapInstance.current) return
            const mapView = mapInstance.current.getView()
            mapView.animate({
              center: fromLonLat([coords.longitude, coords.latitude]),
              zoom: 17,
              duration: 800,
            })
          },
          () => {}
        )
      }
    }
  }, [query])

  return (
    <MapCoreManagerContext.Provider
      value={{
        //BASE LAYER
        baseLayerSourceName,
        setBaseLayerSourceName,
        // GEOLOCATION
        geolocation,
        setGeolocation,
        //MAP
        mapInstance,
        mapRef,
        view,
        markingData,
        queryingMarkingData,
        selectedEntity,
        setSelectedEntity,
        //PROPS
        selectionMode,
        simpleBaseLayerToogleButton,
        searchField,
        markingDataCard,
        navbar,
        goToMyLocation,
        //FUNCTIONS
        _getMarkingData,
      }}
    >
      {children}
    </MapCoreManagerContext.Provider>
  )
}

export const useMapCoreManager = () => useContext(MapCoreManagerContext)

export default MapCoreManagerProvider
